import { useContext, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ResultTableItem from '../../../molecules/screener/result-table-item';
import ResultTableHeader from '../../../molecules/screener/result-table-header';
import { fetchScreenerData, updateSort } from '../../../../stores/screener/screener-redux-store';
import ScreenerAppContext from '../../../../stores/screener/screener-app-context';
import InteractiveElement from '../../../atoms/interactive-element';
import { SignatureResults } from '../../../organisms/screener/signature-results';
import { getWindowDimensions, sortBy } from '../../../../utils/sharedHelpers';
import _debounce from 'lodash/debounce';
import { resetFilters, setInitialState } from '../../../../stores/screener/actions/filter-actions';

import styles from './results-table.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { CHANNELS, DEFAULT_VIEWS } from '../../../../shared/screener-constants';

const className = augmentClassName(styles);

export const ResultsTable = ({
  initState,
  getScreenerData,
  resultData,
  filters,
  slideUp,
  viewResult,
  updateSortByHeader,
  showOnlyMyHoldings,
  resetAllFilters,
  performanceQuarterly,
  setPerformanceQuarterly,
  planApiLoading,
  isValidPlan
}) => {
  const isInitialMount = useRef(true);

  const { yyy, channel, defaultView, bluetagcharacteristic } = useContext(ScreenerAppContext);
  const [expandStateAllResults, setExpandStateAllResults] = useState([]);
  const [expandStateSignatureResults, setExpandStateSignatureResults] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const [isMobile, setIsMobile] = useState(getWindowDimensions()?.width <= 576);
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(getWindowDimensions()?.width < 1024);
  const [sortingState, setSortingState] = useState({
    sortBy: 'threeYear',
    sortDirection: 'desc'
  });
  const [showSalesCharge, setShowSalesCharge] = useState(false);

  const onChangePerformanceTerm = value => {
    setPerformanceQuarterly(value);
    viewResult();
  };

  const onClickExpandAll = () => {
    let expand = true;
    let updatedExpandState = [...expandStateAllResults];
    expand = !expandStateAllResults.every(el => el === true);
    updatedExpandState = updatedExpandState.map(el => expand);
    setExpandStateAllResults(updatedExpandState);
    setExpandAll(expand);

    if (resultData.signatureResults?.length > 0) {
      let expandSignatureResults = true;
      let updatedExpandStateSR = [...expandStateSignatureResults];
      expandSignatureResults = !expandStateSignatureResults.every(el => el === true);
      updatedExpandStateSR = updatedExpandStateSR.map(el => expandSignatureResults);
      setExpandStateSignatureResults(updatedExpandStateSR);
    }
  };

  const onClickExpandRow = index => {
    const updatedExpandState = [...expandStateAllResults];
    updatedExpandState[index] = !updatedExpandState[index];
    setExpandStateAllResults(updatedExpandState);
  };

  const onClickExpandRowSignatureResults = index => {
    const updatedExpandStateSR = [...expandStateSignatureResults];
    updatedExpandStateSR[index] = !updatedExpandStateSR[index];
    setExpandStateSignatureResults(updatedExpandStateSR);
  };

  const [isResultUpdated, setIsResultUpdated] = useState(true);

  useEffect(() => {
    const resultLength = resultData?.screenerData?.length;
    const signatureResultLength = resultData?.signatureResults?.length;
    if (resultLength) {
      setExpandStateAllResults(new Array(resultLength).fill(false));
    }
    if (signatureResultLength) {
      setExpandStateSignatureResults(new Array(signatureResultLength).fill(false));
    }
    setIsResultUpdated(true);
  }, [resultData]);

  useEffect(() => {
    if (isInitialMount.current && defaultView !== DEFAULT_VIEWS.NONE) {
      isInitialMount.current = false;
      initState(defaultView);
    }
    if (isResultUpdated && !planApiLoading && isValidPlan) {
      setIsResultUpdated(false);
      getScreenerData(
        yyy,
        channel,
        sortingState,
        false,
        performanceQuarterly,
        defaultView,
        bluetagcharacteristic
      );
    }
  }, [filters, planApiLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWindowResize = () => {
    setIsMobile(getWindowDimensions().width <= 576);
    setIsTabletOrMobile(getWindowDimensions().width < 1024);
  };

  useEffect(() => {
    const resize = _debounce(handleWindowResize, 150);
    window.addEventListener('resize', resize);

    return function cleanupListener() {
      window.removeEventListener('resize', resize);
    };
  }, [isTabletOrMobile]);

  const getPerformanceAsOf = () => {
    return performanceQuarterly
      ? resultData.performanceQTRDate ||
          resultData.performanceAvgAnnReturnQTRDate ||
          resultData.performanceRateReturnQTRDate
      : resultData.performanceMonthlyDate ||
          resultData.performanceAvgAnnReturnMonthlyDate ||
          resultData.performanceRateReturnMonthlyDate;
  };

  const sortByHeader = header => {
    setSortingState(sortBy(header, sortingState));
    viewResult();
  };

  useEffect(() => {
    updateSortByHeader(sortingState, performanceQuarterly, showSalesCharge);
  }, [sortingState, performanceQuarterly, showSalesCharge]); // eslint-disable-line react-hooks/exhaustive-deps

  const showSignatureResults = shouldShowSignatureResults({
    channel,
    showOnlyMyHoldings,
    resultData,
    defaultView
  });
  if (!slideUp) {
    return <ResultCount resultData={resultData} showOnlyMyHoldings={showOnlyMyHoldings} />;
  } else if (resultData.error) {
    return (
      <div {...className('table-container')} role="main">
        <div {...className('table-wrapper')} id="result-table">
          <ResultCount resultData={resultData} showOnlyMyHoldings={showOnlyMyHoldings} />
          {!isTabletOrMobile && (
            <ResultTableHeader
              performanceQuarterly={performanceQuarterly}
              onChangePerformanceTerm={onChangePerformanceTerm}
              onClickExpandAll={onClickExpandAll}
              expandAll={expandAll}
              mStarAsOf={resultData.mStarRatingDate}
              performanceAsOf={getPerformanceAsOf()}
              sortingState={sortingState}
              sortByHeader={sortByHeader}
            />
          )}
          <div {...className('no-result')}>
            <div {...className('no-result-title')}>
              The filters you have chosen return no matching results.
            </div>
            <div {...className('no-result-content')}>
              Please try changing your filter selections or{' '}
              <InteractiveElement
                as="span"
                {...className('reset-filter')}
                onClick={resetAllFilters}
              >
                reset all filters
              </InteractiveElement>
              .
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div {...className('table-container')} role="main">
      <div {...className('table-wrapper')} id="result-table">
        <ResultCount resultData={resultData} showOnlyMyHoldings={showOnlyMyHoldings} />
        {!isTabletOrMobile && (
          <ResultTableHeader
            hasClassA={resultData.hasClassA}
            performanceQuarterly={performanceQuarterly}
            onChangePerformanceTerm={onChangePerformanceTerm}
            onClickExpandAll={onClickExpandAll}
            expandAll={expandAll}
            mStarAsOf={resultData.mStarRatingDate}
            performanceAsOf={getPerformanceAsOf()}
            setShowSalesCharge={setShowSalesCharge}
            showSalesCharge={showSalesCharge}
            sortingState={sortingState}
            sortByHeader={sortByHeader}
          />
        )}
        {showSignatureResults && (
          <SignatureResults
            data={resultData.signatureResults}
            expandState={expandStateSignatureResults}
            onClickExpandRow={onClickExpandRowSignatureResults}
            performanceQuarterly={performanceQuarterly}
            performanceAsOf={getPerformanceAsOf()}
            onChangePerformanceTerm={onChangePerformanceTerm}
            isTabletOrMobile={isTabletOrMobile}
            isMobile={isMobile}
            showOnlyMyHoldings={showOnlyMyHoldings}
          />
        )}
        <h3 {...className('px-6', 'mb-4', 'mt-5', 'heading')}>All results</h3>
        {resultData.screenerData.map((row, index) => {
          if (showOnlyMyHoldings) {
            return (
              row.isOwned && (
                <ResultTableItem
                  data-key={`${row.TCWSODIssue || ''}-${index}`}
                  key={`${row.TCWSODIssue || ''}-${index}`}
                  index={index}
                  item={row}
                  isExpanded={expandStateAllResults[index]}
                  onClickExpandRow={onClickExpandRow}
                  performanceQuarterly={performanceQuarterly}
                  performanceAsOf={getPerformanceAsOf()}
                  isTabletOrMobile={isTabletOrMobile}
                  isMobile={isMobile}
                  onChangePerformanceTerm={onChangePerformanceTerm}
                  showSalesCharge={showSalesCharge}
                />
              )
            );
          }
          return (
            <ResultTableItem
              data-key={`${row.TCWSODIssue || ''}-${index}`}
              key={`${row.TCWSODIssue || ''}-${index}`}
              index={index}
              item={row}
              isExpanded={expandStateAllResults[index]}
              onClickExpandRow={onClickExpandRow}
              performanceQuarterly={performanceQuarterly}
              performanceAsOf={getPerformanceAsOf()}
              isTabletOrMobile={isTabletOrMobile}
              isMobile={isMobile}
              onChangePerformanceTerm={onChangePerformanceTerm}
              showSalesCharge={showSalesCharge}
            />
          );
        })}
      </div>
    </div>
  );
};

ResultsTable.propTypes = {
  filters: PropTypes.object,
  getScreenerData: PropTypes.func,
  initState: PropTypes.func,
  performanceQuarterly: PropTypes.bool,
  setPerformanceQuarterly: PropTypes.func,
  resetAllFilters: PropTypes.func,
  resultData: PropTypes.object,
  showOnlyMyHoldings: PropTypes.bool,
  slideUp: PropTypes.bool,
  updateSortByHeader: PropTypes.func,
  viewResult: PropTypes.func,
  planApiLoading: PropTypes.bool,
  isValidPlan: PropTypes.bool
};

function shouldShowSignatureResults({ channel, showOnlyMyHoldings, resultData, defaultView }) {
  if (defaultView !== DEFAULT_VIEWS.NONE) {
    return false;
  }
  if (!resultData?.signatureResults.length > 0) {
    return false;
  }
  if (channel !== CHANNELS.MYTIAA) {
    return true;
  }
  if (showOnlyMyHoldings) {
    const signatureResultsContainInvestedFunds =
      resultData?.signatureResults?.findIndex(row => row['isOwned']) > -1;
    return signatureResultsContainInvestedFunds;
  }
  return true;
}

const ResultCount = ({ resultData, showOnlyMyHoldings }) => {
  const count = showOnlyMyHoldings ? resultData?.ownedResultsCount : resultData?.totalResults;
  return (
    <div {...className('explore-results-title')} id="result-count">
      Explore {resultData.error ? '' : count} investment results
    </div>
  );
};

ResultCount.propTypes = {
  resultData: PropTypes.object,
  showOnlyMyHoldings: PropTypes.bool
};

const mapStateToProps = state => {
  return {
    resultData: state.resultData,
    showOnlyMyHoldings: state.filterReducer.toggles.showOnlyMyHoldings,
    filters: state.filterReducer.filters,
    planApiLoading: state.filterReducer.planApiLoading,
    isValidPlan: state.filterReducer.planApiError === ''
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getScreenerData: (
      yyy,
      channel,
      sortingState,
      distributions,
      performanceQuarterly,
      defaultView,
      bluetagcharacteristic
    ) =>
      dispatch(
        fetchScreenerData({
          yyy,
          channel,
          sortingState,
          distributions,
          performanceQuarterly,
          defaultView,
          bluetagcharacteristic
        })
      ),
    updateSortByHeader: (sortingState, performanceQuarterly, showSalesCharge) =>
      dispatch(updateSort(sortingState, performanceQuarterly, showSalesCharge)),
    resetAllFilters: () => dispatch(resetFilters()),
    initState: defaultView => dispatch(setInitialState(defaultView))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultsTable);
